import {
  OfflineDownloadSortOptionEnum,
  OfflineDownloadStatusEnum,
  OfflineDownloadTypeEnum,
} from "@/enums/offlineDownload";
import { filterNestedObjectArrayBySearchText, sortNestedObjectArrayByField } from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { OfflineDownloadItem, OfflineDownloadStore } from "@/types/offlineDownload";
import { defineStore } from "pinia";
import { useServerClockStore } from "./serverClockStore";
import { removeDataDirectory } from "@/methods/offlineDownload";

function getInitialOfflineDownloadStore(): OfflineDownloadStore {
  const initialState: OfflineDownloadStore = {
    Downloads: [],
    SelectedType: OfflineDownloadTypeEnum.ALL,
    SearchText: "",
    SortField: OfflineDownloadSortOptionEnum.DOWNLOAD_DATE,
    IsDescendingSort: true,
  };
  return JSON.parse(JSON.stringify(initialState));
}

export const useOfflineDownloadStore = defineStore("offlineDownloadStore", {
  state: (): OfflineDownloadStore => getInitialOfflineDownloadStore(),
  getters: {
    CompletedDownloads(): Array<OfflineDownloadItem> {
      return this.Downloads.filter((d) => d.Status === OfflineDownloadStatusEnum.COMPLETED).sort(
        (a, b) => new Date(b.DownloadedOn).getTime() - new Date(a.DownloadedOn).getTime(),
      );
    },
    FilteredDownloads(): Array<OfflineDownloadItem> {
      let result = this.CompletedDownloads as Array<OfflineDownloadItem>;
      if (this.SelectedType !== OfflineDownloadTypeEnum.ALL) {
        result = result.filter((d) => d.Type === this.SelectedType);
      }
      result = filterNestedObjectArrayBySearchText(result, ["Name"], this.SearchText) ?? result;
      result =
        sortNestedObjectArrayByField(result, this.SortField, this.IsDescendingSort) ?? result;
      return result;
    },
    TotalStorageUsedBytes(): number {
      return this.Downloads.filter((d) => d.Status === OfflineDownloadStatusEnum.COMPLETED).reduce(
        (sum, d) => sum + d.TotalSizeBytes,
        0,
      );
    },
    TotalStorageUsedDisplay(): string {
      const bytes = this.TotalStorageUsedBytes;
      if (bytes === 0) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      const size = (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0);
      return `${size} ${units[i]}`;
    },
    DownloadCount(): number {
      return this.CompletedDownloads.length;
    },
  },
  actions: {
    isAlreadyDownloaded(name: string, type: string): boolean {
      return this.Downloads.some(
        (d) =>
          d.Name === name && d.Type === type && d.Status === OfflineDownloadStatusEnum.COMPLETED,
      );
    },
    getDownloadById(id: string): OfflineDownloadItem | undefined {
      return this.Downloads.find((d) => d.Id === id);
    },
    addDownload(item: OfflineDownloadItem) {
      this.Downloads = this.Downloads.filter(
        (d) =>
          !(
            d.Name === item.Name &&
            d.Type === item.Type &&
            d.Status === OfflineDownloadStatusEnum.FAILED
          ),
      );
      this.Downloads.push(item);
    },
    updateDownloadStatus(id: string, status: OfflineDownloadStatusEnum) {
      const download = this.Downloads.find((d) => d.Id === id);
      if (download) {
        download.Status = status;
      }
    },
    updateDownloadSize(id: string, totalSizeBytes: number) {
      const download = this.Downloads.find((d) => d.Id === id);
      if (download) {
        download.TotalSizeBytes = totalSizeBytes;
      }
    },
    async removeDownload(id: string) {
      const download = this.Downloads.find((d) => d.Id === id);
      if (!download) {
        return;
      }
      const appSettingsStore = useAppSettingsStore();
      if (appSettingsStore.IsNativePlatform) {
        await removeDataDirectory(download.DirectoryPath);
      }
      this.Downloads = this.Downloads.filter((d) => d.Id !== id);
    },
    async removeAllDownloads() {
      const appSettingsStore = useAppSettingsStore();
      if (appSettingsStore.IsNativePlatform) {
        for (const download of this.Downloads) {
          await removeDataDirectory(download.DirectoryPath);
        }
        await removeDataDirectory("offline-downloads");
      }
      this.Downloads = [];
    },
    async cleanupStaleDownloads() {
      const serverClockStore = useServerClockStore();
      const { getServerTimestamp } = serverClockStore;
      const currentServerTime = await getServerTimestamp();
      const oneDayAgo = currentServerTime - 24 * 60 * 60 * 1000;
      this.Downloads = this.Downloads.filter((d) => {
        if (d.Status === OfflineDownloadStatusEnum.COMPLETED) return true;
        const downloadTime = new Date(d.DownloadedOn).getTime();
        return downloadTime > oneDayAgo;
      });
    },
  },
  persist: true,
});
